import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { toast } from '../../lib/toast'
import { BOOKME_ONBOARDING_URL } from '../../lib/product'

const SETTING_KEYS = [
  'booking_slug',
  'bookme_host_display_name',
  'bookme_meeting_title',
  'bookme_meeting_duration_min',
  'bookme_working_hours',
  'bookme_timezone',
  'bookme_buffer_min',
] as const

type SettingKey = (typeof SETTING_KEYS)[number]

interface WorkingHours {
  start: string
  end: string
  days: number[]
}

interface FormState {
  slug: string
  displayName: string
  meetingTitle: string
  durationMin: string
  whStart: string
  whEnd: string
  whDays: number[]
  timezone: string
  bufferMin: string
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const DEFAULTS: FormState = {
  slug: '',
  displayName: '',
  meetingTitle: 'Intro',
  durationMin: '30',
  whStart: '09:00',
  whEnd: '17:00',
  whDays: [1, 2, 3, 4, 5],
  timezone:
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' : 'UTC',
  bufferMin: '0',
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/

function isValidTimezone(tz: string): boolean {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

function parseWorkingHours(raw: string | null | undefined): WorkingHours | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed.start === 'string' &&
      typeof parsed.end === 'string' &&
      Array.isArray(parsed.days)
    ) {
      const days = (parsed.days as unknown[])
        .map((d) => Number(d))
        .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
      return { start: parsed.start, end: parsed.end, days }
    }
  } catch {
    // fall through
  }
  return null
}

interface Props {
  userId: string
  /** Notifies parent when slug changes so the bookings empty state stays in sync. */
  onSlugChange?: (slug: string) => void
}

export function BookMeSettingsCard({ userId, onSlugChange }: Props) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [form, setForm] = useState<FormState>(DEFAULTS)
  const [savedSlug, setSavedSlug] = useState<string>('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      const { data } = await supabase
        .from('user_settings')
        .select('setting_key, setting_value')
        .eq('user_id', userId)
        .in('setting_key', SETTING_KEYS as unknown as string[])
      if (cancelled) return
      const map: Partial<Record<SettingKey, string>> = {}
      for (const row of (data ?? []) as Array<{ setting_key: SettingKey; setting_value: string }>) {
        map[row.setting_key] = row.setting_value
      }
      const wh = parseWorkingHours(map.bookme_working_hours)
      const slug = (map.booking_slug ?? '').trim()
      const next: FormState = {
        slug,
        displayName: (map.bookme_host_display_name ?? DEFAULTS.displayName).trim(),
        meetingTitle: (map.bookme_meeting_title ?? DEFAULTS.meetingTitle).trim(),
        durationMin: map.bookme_meeting_duration_min ?? DEFAULTS.durationMin,
        whStart: wh?.start ?? DEFAULTS.whStart,
        whEnd: wh?.end ?? DEFAULTS.whEnd,
        whDays: wh?.days ?? DEFAULTS.whDays,
        timezone: (map.bookme_timezone ?? DEFAULTS.timezone).trim(),
        bufferMin: map.bookme_buffer_min ?? DEFAULTS.bufferMin,
      }
      setForm(next)
      setSavedSlug(slug)
      // Stay collapsed: without a handle the Calendar screen leads with
      // "Turn on your booking page" (the setup wizard); "Set up here"
      // opens this form for doing it in place.
      setExpanded(false)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  const publicUrl = useMemo(() => {
    if (!savedSlug) return null
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}/book/${savedSlug}`
  }, [savedSlug])

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function toggleDay(day: number) {
    setForm((prev) => {
      const has = prev.whDays.includes(day)
      const next = has ? prev.whDays.filter((d) => d !== day) : [...prev.whDays, day]
      next.sort((a, b) => a - b)
      return { ...prev, whDays: next }
    })
  }

  function validate(): string | null {
    const slug = form.slug.trim().toLowerCase()
    if (!slug) return 'Choose a handle for your booking page.'
    if (!SLUG_RE.test(slug)) {
      return 'Handle must be 3-40 lowercase letters / numbers / dashes, starting and ending with a letter or number.'
    }
    if (!form.displayName.trim()) return 'Add your display name.'
    if (!form.meetingTitle.trim()) return 'Add a meeting title.'
    const dur = Number(form.durationMin)
    if (!Number.isInteger(dur) || dur < 5 || dur > 240) {
      return 'Duration must be a whole number of minutes between 5 and 240.'
    }
    if (!/^\d{2}:\d{2}$/.test(form.whStart) || !/^\d{2}:\d{2}$/.test(form.whEnd)) {
      return 'Working hours must be in HH:MM format.'
    }
    if (form.whStart >= form.whEnd) {
      return 'Working hours end must be after start.'
    }
    if (form.whDays.length === 0) {
      return 'Select at least one working day.'
    }
    if (!isValidTimezone(form.timezone.trim())) {
      return 'Timezone must be a valid IANA name (e.g. Asia/Kolkata, America/New_York).'
    }
    const buf = Number(form.bufferMin)
    if (!Number.isInteger(buf) || buf < 0 || buf > 120) {
      return 'Buffer must be a whole number of minutes between 0 and 120.'
    }
    return null
  }

  async function handleSave() {
    const err = validate()
    if (err) {
      toast(err, 'error')
      return
    }
    const slug = form.slug.trim().toLowerCase()
    setSaving(true)
    const rows = [
      { setting_key: 'booking_slug', setting_value: slug },
      { setting_key: 'bookme_host_display_name', setting_value: form.displayName.trim() },
      { setting_key: 'bookme_meeting_title', setting_value: form.meetingTitle.trim() },
      {
        setting_key: 'bookme_meeting_duration_min',
        setting_value: String(Number(form.durationMin)),
      },
      {
        setting_key: 'bookme_working_hours',
        setting_value: JSON.stringify({
          start: form.whStart,
          end: form.whEnd,
          days: form.whDays,
        }),
      },
      { setting_key: 'bookme_timezone', setting_value: form.timezone.trim() },
      { setting_key: 'bookme_buffer_min', setting_value: String(Number(form.bufferMin)) },
    ].map((r) => ({
      ...r,
      user_id: userId,
      updated_at: new Date().toISOString(),
    }))

    const { error } = await supabase
      .from('user_settings')
      .upsert(rows, { onConflict: 'user_id,setting_key' })
    setSaving(false)
    if (error) {
      // Slug uniqueness collision surfaces as a 23505 conflict against the
      // partial UNIQUE index added in migration 20260507000006.
      const msg = error.message || ''
      if (/duplicate key|already exists|unique/i.test(msg)) {
        toast('That handle is already taken — try another.', 'error')
      } else {
        toast(`Save failed: ${msg}`, 'error')
      }
      return
    }
    setSavedSlug(slug)
    onSlugChange?.(slug)
    toast('Booking page saved', 'success')
  }

  function handleCopy() {
    if (!publicUrl) return
    void navigator.clipboard
      .writeText(publicUrl)
      .then(() => toast('Booking link copied', 'success'))
      .catch(() => toast('Could not copy link', 'error'))
  }

  if (loading) {
    return (
      <div
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '18px 20px',
          marginBottom: 20,
        }}
        aria-busy="true"
      >
        <span className="skel w40" style={{ height: 16 }} />
      </div>
    )
  }

  return (
    <div
      style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '18px 20px',
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-ui)',
              marginBottom: 6,
            }}
          >
            Booking page
          </div>
          {publicUrl ? (
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                color: 'var(--text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={publicUrl}
            >
              {publicUrl.replace(/^https?:\/\//, '')}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              Off. Turn it on with the{' '}
              <a href={BOOKME_ONBOARDING_URL} style={{ color: 'var(--accent)' }}>
                two-minute setup
              </a>
              , or set it up here.
            </div>
          )}
        </div>
        {/*
          MIGRATION_DEBT: small-button cluster (Copy / Open / Edit).
          Current implementation: legacy `.sec-btn` class + inline
          `fontSize: 11` override, which violates the no-inline-font-size
          rule in shared/ui/README.md.

          owner: @shaun
          replace with: <Button variant="secondary" size="sm"> from shared/ui
          remove when:
            (a) a second legitimate consumer for size="sm" surfaces, AND
            (b) owner signs off on either adding `.btn-sm` to legacy CSS
                or letting Button own size CSS in our bundle.

          Until both conditions are met, this cluster intentionally stays
          on legacy classes. Primitive multiplication pressure resisted
          per PR-3 outcome — see DEVELOPMENT_PROGRESS.
        */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {publicUrl && (
            <>
              <button
                type="button"
                className="sec-btn"
                style={{ fontSize: 11 }}
                onClick={handleCopy}
              >
                Copy link
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="sec-btn"
                style={{ fontSize: 11, textDecoration: 'none' }}
              >
                Open
              </a>
            </>
          )}
          <button
            type="button"
            className="sec-btn"
            style={{ fontSize: 11 }}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? 'Close' : publicUrl ? 'Edit' : 'Set up here'}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 18, display: 'grid', gap: 14 }}>
          <Field
            label="Handle"
            hint="Lowercase letters, numbers, dashes. Used in the public URL: /book/<handle>."
          >
            <input
              className="settings-input"
              value={form.slug}
              onChange={(e) => setField('slug', e.target.value)}
              placeholder="shaun"
              autoComplete="off"
              spellCheck={false}
            />
          </Field>

          <Field
            label="Your display name"
            hint="Shown to bookers on the booking page and in confirmation emails."
          >
            <input
              className="settings-input"
              value={form.displayName}
              onChange={(e) => setField('displayName', e.target.value)}
              placeholder="Shaun Godinho"
            />
          </Field>

          <Field
            label="Meeting title"
            hint='e.g. "Intro with Shaun" — appears in email subjects and calendar invites.'
          >
            <input
              className="settings-input"
              value={form.meetingTitle}
              onChange={(e) => setField('meetingTitle', e.target.value)}
              placeholder="Intro with Shaun"
            />
          </Field>

          <Field label="Duration" hint="Length of each booking, in minutes.">
            <input
              className="settings-input"
              type="number"
              min={5}
              max={240}
              step={5}
              value={form.durationMin}
              onChange={(e) => setField('durationMin', e.target.value)}
              style={{ width: 100 }}
            />
          </Field>

          <Field label="Working days" hint="Days of the week the booking page offers slots.">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DAY_LABELS.map((label, i) => {
                const active = form.whDays.includes(i)
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border2)'),
                      background: active ? 'var(--accent-glow)' : 'var(--bg)',
                      color: active ? 'var(--accent)' : 'var(--text-dim)',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 11,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                    }}
                    aria-pressed={active}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </Field>

          <Field label="Hours" hint="Start / end of your booking window in your timezone.">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className="settings-input"
                type="time"
                value={form.whStart}
                onChange={(e) => setField('whStart', e.target.value)}
                style={{ width: 130 }}
              />
              <span style={{ color: 'var(--text-dim)' }}>to</span>
              <input
                className="settings-input"
                type="time"
                value={form.whEnd}
                onChange={(e) => setField('whEnd', e.target.value)}
                style={{ width: 130 }}
              />
            </div>
          </Field>

          <Field
            label="Timezone"
            hint='IANA name (e.g. "Asia/Kolkata", "America/New_York"). Working hours and slot generation use this.'
          >
            <input
              className="settings-input"
              value={form.timezone}
              onChange={(e) => setField('timezone', e.target.value)}
              placeholder="Asia/Kolkata"
              autoComplete="off"
              spellCheck={false}
            />
          </Field>

          <Field
            label="Buffer between bookings"
            hint="Minutes of breathing room after each meeting before the next slot opens."
          >
            <input
              className="settings-input"
              type="number"
              min={0}
              max={120}
              step={5}
              value={form.bufferMin}
              onChange={(e) => setField('bufferMin', e.target.value)}
              style={{ width: 100 }}
            />
          </Field>

          {/*
            MIGRATION_DEBT: non-toolbar primary Save button.
            Current implementation: legacy `.btn-save` class, which has
            2px/side more horizontal padding than `.add-btn` (the class
            `<Button variant="primary">` wraps). Adopting Button here
            would shrink the button — breaking visual parity.

            owner: @shaun
            replace with: <Button variant="primary"> after a non-toolbar
                          primary variant is justified
            remove when: a second non-toolbar primary consumer surfaces
                         (likely Compose's Send button after the Modal
                         primitive ships in Phase 5).
          */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" className="btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="settings-label" style={{ marginBottom: 4 }}>
        {label}
      </div>
      {hint && (
        <div className="settings-hint" style={{ marginBottom: 8 }}>
          {hint}
        </div>
      )}
      {children}
    </div>
  )
}
